import type { IconButtonProps as ChakraIconButtonProps } from '@chakra-ui/react';
import React, { cloneElement, isValidElement } from 'react';

import type { OmitChakraProps } from '../../utils/typeUtils';
import { withChakraAs } from '../../utils/withChakraAs';
import type { ButtonProps } from '../Button/Button';
import { Button } from '../Button/Button';

export interface IconButtonProps
  extends OmitChakraProps<
      ChakraIconButtonProps,
      | 'iconSpacing'
      | 'spinner'
      | 'spinnerPlacement'
      | 'styleConfig'
      | 'orientation'
      | 'colorScheme'
      | 'variant'
      | 'size'
      | 'disabled'
      | 'loading'
    >,
    Pick<ButtonProps, 'size' | 'variant' | '__orbitStyleConfig'> {
  /**
   * Use `isDisabled` instead
   */
  disabled?: never;

  /**
   * Use `isLoading` instead
   */
  loading?: never;
}

export const IconButton = withChakraAs<IconButtonProps, 'button'>((props) => {
  const { icon, children, isRound, 'aria-label': ariaLabel, ...rest } = props;
  /**
   * Passing the icon as prop or children should work
   */
  const element = icon || children;
  const _children = isValidElement(element)
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cloneElement(element as any, {
        'aria-hidden': true,
        focusable: false,
      })
    : null;

  return (
    <Button
      padding="0"
      borderRadius={isRound ? 'full' : undefined}
      aria-label={ariaLabel}
      {...rest}
    >
      {_children}
    </Button>
  );
});
